// App.ts: Orchestrates simulation and UI, supports extensibility for research/education.
// Purpose: Modular entry point for simulation, visualization, and psychic potential comparison.
// Extensibility: Supports plugins, export/import, and custom research logic.

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { Node } from './types';
import { UI } from './UI';
import { comparePsychicPotential } from './PsychicPotential';
import { ParameterRegistry, Parameter } from './core/parameterRegistry';
import { PixelMapPanel } from './ui/PixelMapPanel';
import { getPerformanceTrail } from './core/performanceMonitor';
import { getKeyboardShortcutSystem } from './core/keyboardShortcutSystem';
import { getSaveLoadSystem } from './core/saveLoadSystem';

export class App {
	network: NeuralNetworkSimulator & {
		runPlugins: () => void;
		hasFailed: () => boolean;
		startSimulation: () => void;
		simulateEvolution: () => void;
		cycle: () => { nodes: import('./types').Node[]; edges: import('./types').Edge[]; branches: number[][]; waves: import('./types').Wave[]; step: number };
		registerPlugin: (plugin: (sim: NeuralNetworkSimulator) => void) => void;
		removeCorrodedNodes: () => void;
	};
	ui: UI;
	loopActive: boolean = true;
	private manuallyPaused: boolean = false;
	private paramRegistry: ParameterRegistry;
	private pixelPanel: PixelMapPanel;

	constructor() {
		// ── PerformanceTrail setup ──
		const pt = getPerformanceTrail();
		pt.budget('sim:cycle', 16);
		pt.budget('sim:startup', 2);
		pt.budget('sim:physics', 5);
		pt.budget('sim:mergeBranches', 2);
		pt.budget('sim:evolution', 5);
		pt.budget('sim:waves', 3);
		pt.budget('sim:energize', 10);
		pt.budget('sim:musical', 3);
		pt.budget('sim:breathing', 2);
		pt.budget('sim:limbs', 2);
		pt.budget('sim:language', 2);
		pt.budget('sim:conceptual', 2);
		pt.budget('sim:brainCreativity', 3);
		pt.budget('sim:diffEvo', 4);
		pt.budget('sim:plugins', 1);
		pt.budget('sim:audio', 2);
		pt.budget('sim:archetypeInternal', 3);
		pt.budget('render', 8);
		pt.budget('render:visEvo', 1);
		pt.budget('render:stats', 2);
		pt.budget('render:projection', 3);
		pt.budget('render:atmosphere', 1);
		pt.budget('render:energyFields', 1);
		pt.budget('render:edges', 5);
		pt.budget('render:energyFlows', 2);
		pt.budget('render:nodes', 6);
		pt.budget('render:psychic', 5);
		pt.budget('render:sounds', 2);
		pt.budget('archetype-update', 5);
		pt.budget('params-update', 2);
		pt.budget('pixel-draw', 3);
		pt.budget('plugins', 4);
		pt.setThrottle('sim:cycle', 2000);
		pt.setThrottle('sim:physics', 3000);
		pt.setThrottle('sim:evolution', 3000);
		pt.setThrottle('sim:waves', 3000);
		pt.setThrottle('sim:energize', 3000);
		pt.setThrottle('render', 2000);
		pt.registerMetricsProvider(() => {
			const mem = (performance as any).memory;
			const lodStatus = this.ui?.getLODStatus?.() || { tier: '?', fps: 0, degraded: false, frameSkip: 1 };
			return {
				frameTime: 0,
				jsHeapSize: mem?.usedJSHeapSize ?? 0,
				jsHeapLimit: mem?.jsHeapSizeLimit ?? 0,
				totalNodes: this.network?.nodes?.length ?? 0,
				totalEdges: this.network?.edges?.length ?? 0,
			};
		});
		// Listen for keyboard shortcut to toggle perf panel
		window.addEventListener('debug:togglePerf', () => pt.togglePanel());
		// Instantiate keyboard shortcut system so shortcuts fire
		getKeyboardShortcutSystem();

		this.network = new NeuralNetworkSimulator();
		// Add these lines to ensure optional methods exist
		this.network.removeCorrodedNodes = this.network.removeCorrodedNodes || (() => {});
		this.network.runPlugins = this.network.runPlugins || (() => {});
		this.network.hasFailed = this.network.hasFailed || (() => false);
		this.network.startSimulation = this.network.startSimulation || (() => {});
		this.network.simulateEvolution = this.network.simulateEvolution || (() => {});
		this.network.cycle = this.network.cycle || (() => ({}));
		this.network.registerPlugin = this.network.registerPlugin || (() => {});
		this.ui = new UI(this.network);

		// ── Save/Load: Ctrl+S quicksave, Ctrl+O quickload ──
		const saveLoad = getSaveLoadSystem();
		window.addEventListener('keydown', (e) => {
			if (e.ctrlKey && e.key === 's') {
				e.preventDefault();
				try {
					const snapshot = this.network.exportState?.() || JSON.stringify({ nodes: this.network.nodes, edges: this.network.edges, step: this.network.step });
					saveLoad.quickSave({ metadata: { version: '2.0', timestamp: Date.now(), nodeCount: this.network.nodes.length, edgeCount: this.network.edges.length, simulationStep: this.network.step, appVersion: '1.0' }, network: { nodes: this.network.nodes, edges: this.network.edges } });
					console.log('[Save] Quicksaved');
				} catch (err) { console.error('[Save] Failed:', err); }
			}
			if (e.ctrlKey && e.key === 'o') {
				e.preventDefault();
				const saves = saveLoad.listSaves();
				const latest = saves.filter((s: string) => s.startsWith('quicksave_')).sort().pop();
				if (latest) {
					try {
						const data = saveLoad.load(latest);
						if (data) {
							this.network.importState?.(JSON.stringify(data.network));
							console.log('[Load] Quickloaded:', latest);
						}
					} catch (err) { console.error('[Load] Failed:', err); }
				}
			}
		});

		// Parameter visualization setup
		this.paramRegistry = new ParameterRegistry();
		this.pixelPanel = new PixelMapPanel(260, 6);
		// Providers: simulator
		this.paramRegistry.registerProvider(() => {
		const arr: Parameter[] = [];
		const nodeCount = this.network.nodes?.length || 0;
		const edgeCount = this.network.edges?.length || 0;
		const totalEnergy = this.network.nodes?.reduce((s, n) => s + (n.energy || 0), 0) || 0;
			arr.push({ id: 'sim.step', name: 'Step', category: 'simulation', type: 'numeric', value: this.network.step, min: 0 });
			arr.push({ id: 'sim.nodes', name: 'Nodes', category: 'simulation', type: 'numeric', value: nodeCount, min: 0 });
			arr.push({ id: 'sim.edges', name: 'Edges', category: 'simulation', type: 'numeric', value: edgeCount, min: 0 });
			arr.push({ id: 'sim.energy.total', name: 'Total Energy', category: 'energy', type: 'numeric', value: totalEnergy, min: 0 });
			arr.push({ id: 'sim.stability', name: 'Stability', category: 'dynamics', type: 'numeric', value: (this.network as any).stabilityMetric || 0, min: 0, max: 1 });
			return arr;
		});
		// Providers: breathing
		this.paramRegistry.registerProvider(() => {
			// Access breathing internals via network — state is private in BreathingSystem
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const b = (this.network as any).breathingSystem as
				{ state?: { oxygenLevel?: number; carbonDioxideLevel?: number; respiratoryRate?: number; cyclePhase?: string; fatigueLevel?: number; stressLevel?: number } } | undefined;
			if (!b?.state) return [];
			const s = b.state;
			return [
				{ id: 'breath.oxygen', name: 'Oxygen Level', category: 'breathing', type: 'numeric', value: s.oxygenLevel ?? 0, min: 0, max: 1 },
				{ id: 'breath.co2', name: 'CO2 Level', category: 'breathing', type: 'numeric', value: s.carbonDioxideLevel ?? 0, min: 0, max: 1 },
				{ id: 'breath.rate', name: 'Resp Rate', category: 'breathing', type: 'numeric', value: s.respiratoryRate ?? 0, min: 0 },
				{ id: 'breath.phase', name: 'Phase', category: 'breathing', type: 'categorical', value: String(s.cyclePhase ?? 'unknown') },
				{ id: 'breath.fatigue', name: 'Fatigue', category: 'breathing', type: 'numeric', value: s.fatigueLevel ?? 0, min: 0, max: 1 },
				{ id: 'breath.stress', name: 'Stress', category: 'breathing', type: 'numeric', value: s.stressLevel ?? 0, min: 0, max: 1 }
			];
		});
		// Providers: archetypes
		this.paramRegistry.registerProvider(() => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const engine = (this.network as any).archetypeEngine as
				{ getSummary(): { all?: { key: string; name?: string; score?: number }[] } } | undefined;
			if (!engine || typeof engine.getSummary !== 'function') return [];
			const sum = engine.getSummary();
			if (!sum || !Array.isArray(sum.all)) return [];
			return sum.all.map((a: { key: string; name?: string; score?: number }) => ({
				id: `arch.${a.key}`,
				name: a.name || a.key,
				category: 'archetype',
				type: 'numeric',
				value: a.score ?? 0,
				min: 0,
				max: 1
			}));
		});
		// Providers: voice characteristics
		this.paramRegistry.registerProvider(() => {
			const v = this.network.voiceCharacteristics;
			return [
				{ id: 'voice.pitch', name: 'Voice Pitch', category: 'voice', type: 'numeric', value: v.pitch ?? 0, min: 50, max: 500 },
				{ id: 'voice.breathiness', name: 'Breathiness', category: 'voice', type: 'numeric', value: v.breathiness ?? 0, min: 0, max: 1 },
				{ id: 'voice.roughness', name: 'Roughness', category: 'voice', type: 'numeric', value: v.roughness ?? 0, min: 0, max: 1 },
				{ id: 'voice.sustain', name: 'Sustain', category: 'voice', type: 'numeric', value: v.sustainLevel ?? 0, min: 0, max: 1 }
			];
		});
		// Providers: zodiac/element mix from nodes
		this.paramRegistry.registerProvider(() => {
			const arr: Parameter[] = [];
			const nodes = this.network.nodes || [];
			if (nodes.length === 0) return arr;
			const counts: Record<string, number> = {};
			const elemCounts: Record<string, number> = {};
			const toZodiac = (hue: number): string => {
				if (hue >= 60 && hue < 80) return 'Taurus';
				else if (hue >= 80 && hue < 100) return 'Capricorn';
				else if (hue >= 100 && hue < 120) return 'Scorpio';
				else if (hue >= 120 && hue < 150) return 'Cancer';
				else if (hue >= 60 && hue < 150) return 'Virgo';
				else if (hue >= 150 && hue < 200) return 'Gemini';
				else if (hue >= 200 && hue < 260) return 'Aquarius';
				else if (hue >= 260 && hue < 320) return 'Pisces';
				else if (hue >= 320 && hue < 340) return 'Libra';
				else if (hue >= 40 && hue < 60) return 'Sagittarius';
				else if (hue >= 20 && hue < 40) return 'Leo';
				else return 'Aries';
			};
			const zodiacToElement: Record<string, 'Fire'|'Earth'|'Air'|'Water'> = {
				Aries: 'Fire', Leo: 'Fire', Sagittarius: 'Fire',
				Taurus: 'Earth', Virgo: 'Earth', Capricorn: 'Earth',
				Gemini: 'Air', Libra: 'Air', Aquarius: 'Air',
				Cancer: 'Water', Scorpio: 'Water', Pisces: 'Water'
			};
			for (let i = 0; i < nodes.length; i++) {
				const n: Node = nodes[i];
				const color = n.color || '';
				let hue = 0;
				const m = /hsl\(([-\d.]+),/.exec(color);
				if (m) hue = parseFloat(m[1]);
				const z = toZodiac(hue);
				counts[z] = (counts[z] || 0) + 1;
				const elem = zodiacToElement[z];
				if (elem) elemCounts[elem] = (elemCounts[elem] || 0) + 1;
			}
			const topZ = Object.keys(counts).sort((a,b) => (counts[b]||0)-(counts[a]||0))[0];
			if (topZ) arr.push({ id: 'zodiac.dominant', name: 'Dominant Zodiac', category: 'zodiac', type: 'categorical', value: topZ });
			const topE = Object.keys(elemCounts).sort((a,b) => (elemCounts[b]||0)-(elemCounts[a]||0))[0];
			if (topE) arr.push({ id: 'element.dominant', name: 'Dominant Element', category: 'element', type: 'categorical', value: topE });
			const total = nodes.length;
			const elements = ['Fire','Earth','Air','Water'] as const;
			for (let e of elements) {
				const v = (elemCounts[e] || 0) / total;
				arr.push({ id: `element.mix.${e.toLowerCase()}`, name: `${e} Mix`, category: 'element_mix', type: 'numeric', value: v, min: 0, max: 1 });
			}
			return arr;
		});

		// Providers: conceptual abstraction (thought activity)
		this.paramRegistry.registerProvider(() => {
			const arr: Parameter[] = [];
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const cas = (this.network as any).conceptualAbstraction as
				{ conceptualSpace?: { concepts?: Map<string, { energy?: number; power?: number; resonance?: number }> }; getConceptualSpace?(): { concepts?: Map<string, { energy?: number; power?: number; resonance?: number }> } } | undefined;
			if (!cas) return arr;
			const space = cas.conceptualSpace || (cas.getConceptualSpace && cas.getConceptualSpace());
			const concepts = space?.concepts;
			if (concepts && typeof concepts.forEach === 'function') {
				let topId: string | null = null;
				let topEnergy = -Infinity;
				concepts.forEach((c, id: string) => {
					const energy = c?.energy ?? 0;
					arr.push({ id: `concept.energy.${id}`, name: `Concept Energy · ${id}`, category: 'thought', type: 'numeric', value: energy, min: 0, max: 1 });
					const power = c?.power ?? 0;
					arr.push({ id: `concept.power.${id}`, name: `Concept Power · ${id}`, category: 'thought', type: 'numeric', value: power, min: 0, max: 1 });
					const resonance = c?.resonance ?? 0;
					arr.push({ id: `concept.resonance.${id}`, name: `Concept Resonance · ${id}`, category: 'thought', type: 'numeric', value: resonance, min: 0, max: 1 });
					if (energy > topEnergy) { topEnergy = energy; topId = id; }
				});
				if (topId) {
					arr.push({ id: 'concept.dominant', name: 'Dominant Concept', category: 'thought', type: 'categorical', value: topId });
				}
			}
			return arr;
		});

		// Example extensibility: register a plugin for custom research logic
		this.network.registerPlugin((sim) => {
			// Example: log energy stats every 500 steps
			if (sim.step % 500 === 0) {
				const avgEnergy = sim.nodes.reduce((sum, n) => sum + n.energy, 0) / sim.nodes.length;
				console.log(`[App Research] Step ${sim.step}: Avg energy = ${avgEnergy.toFixed(2)}`);
			}
		});

		// Wire up restart button
		this.ui.restartBtn.onclick = () => {
			this.restartSimulation();
		};
		this.ui.playPauseBtn.onclick = () => {
			this.manuallyPaused = !this.manuallyPaused;
			this.ui.playPauseBtn.textContent = this.manuallyPaused ? 'Play Simulation' : 'Pause Simulation';
			this.ui.titleDiv.textContent = this.manuallyPaused ? 'Simulation Paused (Manual)' : 'Simulation Running';
			this.ui.titleDiv.style.color = '#fff';
		};

		this.loopActive = true;
		this.runMainLoop(); // <-- Start main simulation loop
	}

	cycleSimulationData() {
		const pt = getPerformanceTrail();

		pt.start('sim:cycle');
		this.network.cycle();
		const simStepMs = pt.end('sim:cycle');
		pt.updateMetrics({ simulationStepTime: simStepMs });

		pt.start('render');
		this.ui.render();
		const renderMs = pt.end('render');
		pt.updateMetrics({ renderTime: renderMs });

		// Update archetypes and draw pixel map
		if ((this.network as any).archetypeEngine?.updateFromNetwork) {
			pt.start('archetype-update');
			(this.network as any).archetypeEngine.updateFromNetwork(this.network);
			pt.end('archetype-update');
		}

		pt.start('params-update');
		this.paramRegistry.update();
		pt.end('params-update');

		pt.start('pixel-draw');
		this.pixelPanel.draw(this.paramRegistry);
		pt.end('pixel-draw');

		if (typeof this.network.runPlugins === 'function') {
			pt.start('plugins');
			this.network.runPlugins();
			pt.end('plugins');
		}
	}

	runMainLoop() {
		const pt = getPerformanceTrail();
		// ── Fixed-Timestep Accumulator with Frame Interpolation (Glenn Fiedler pattern) ──
		// Simulation runs at a target rate that adapts to node count and measured cycle time.
		// Rendering runs at display refresh rate with interpolated positions for smooth motion.
		const BASE_SIM_HZ = 30;                    // Base sim rate for small networks (<50 nodes)
		const MIN_SIM_DT   = 1000 / BASE_SIM_HZ;   // ~33ms budget
		const MAX_SIM_HZ   = 60;                   // Never faster than 60Hz
		const MAX_FRAME_TIME = 250;                // Clamp to avoid spiral of death on tab-away

		let accumulator = 0;
		let lastTime = performance.now();
		// Adaptive: track cycle time to dynamically adjust sim rate
		let measuredCycleMs = MIN_SIM_DT;
		let simDt = MIN_SIM_DT;

		const loop = (timestamp: number) => {
			// Compute elapsed real time, clamped to avoid spiral of death
			let frameTime = timestamp - lastTime;
			lastTime = timestamp;
			if (frameTime > MAX_FRAME_TIME) frameTime = MAX_FRAME_TIME;

			pt.start('animation-frame');

			if (!this.manuallyPaused) {
				accumulator += frameTime;

				// ── Adaptive sim rate: scale with node count ──
				const nodeCount = this.network.nodes?.length || 0;
				// 0-50: 30Hz, 50-100: ramp to 20Hz, 100-200: ramp to 10Hz, 200+: 8Hz
				const targetHz = nodeCount < 50 ? 30 : nodeCount < 100
					? 30 - (nodeCount - 50) * 0.2   // 30→20
					: nodeCount < 200
						? 20 - (nodeCount - 100) * 0.1 // 20→10
						: Math.max(8, 10 - (nodeCount - 200) * 0.02);
				// Also clamp by measured cycle time (leave 30% headroom)
				const cycleBudget = Math.max(measuredCycleMs * 1.3, 1000 / MAX_SIM_HZ);
				simDt = Math.max(1000 / targetHz, cycleBudget);

				// Consume accumulator in fixed dt steps — SIMULATION ONLY, no render
				let stepsThisFrame = 0;
				const cycleStart = performance.now();
				while (accumulator >= simDt) {
					try {
						pt.start('sim:cycle');
						this.network.cycle();
						const cycleMs = pt.end('sim:cycle');
						// Exponential moving average of cycle time
						measuredCycleMs = measuredCycleMs * 0.9 + cycleMs * 0.1;
					} catch (err) {
						pt.error('App.runMainLoop', 'Simulation error (recovering)', { error: String(err) });
						this.ui.titleDiv.textContent = 'Simulation Recovering...';
						this.ui.titleDiv.style.color = '#f90';
					}
					accumulator -= simDt;
					stepsThisFrame++;
					// Safety: max 4 steps per frame (spiral of death protection)
					if (stepsThisFrame >= 4) { accumulator = 0; break; }
				}

				// ── Interpolation alpha ──
				this.network.interpolationAlpha = Math.min(1.0, accumulator / simDt);
			} else {
				this.network.interpolationAlpha = 1.0;
				accumulator = 0;
			}

			// ── Render ONCE per frame (after simulation catch-up) ──
			try {
				pt.start('render');
				this.ui.render();
				pt.end('render');
			} catch { /* rendering should never block the loop */ }

			// Non-render updates
			try {
				if ((this.network as any).archetypeEngine?.updateFromNetwork) {
					(this.network as any).archetypeEngine.updateFromNetwork(this.network);
				}
			} catch { /* non-critical */ }
			try { this.paramRegistry.update(); } catch { /* non-critical */ }
			try { this.pixelPanel.draw(this.paramRegistry); } catch { /* non-critical */ }
			if (typeof this.network.runPlugins === 'function') {
				try { this.network.runPlugins(); } catch { /* non-critical */ }
			}

			const failed = this.network.hasFailed();
			if (typeof failed === 'boolean' && failed) {
				this.ui.titleDiv.textContent = 'Simulation Failed';
				this.ui.titleDiv.style.color = '#c00';
			}

			pt.end('animation-frame');
			pt.updateMetrics({ frameTime });
			pt.tick();
			requestAnimationFrame(loop);
		};

		requestAnimationFrame(loop);
	}

	init() {
		// Bootstrap complete — simulation loop started in constructor
	}

	showPsychicPotentialComparison(otherNetwork: NeuralNetworkSimulator) {
		const result = comparePsychicPotential(this.network, otherNetwork);
		this.ui.infoDiv.innerHTML += `<br><b>Psychic Potential Comparison:</b> Score: ${result.score.toFixed(2)}, Compatibility: ${(result.compatibility * 100).toFixed(1)}%<br>${result.details}`;
	}

	restartSimulation() {
		this.loopActive = false;
		// Stop all sounds before removing UI
		if (typeof this.ui.restartAllSounds === 'function') {
			this.ui.restartAllSounds();
		}
		// Remove old canvas and UI elements
		document.body.removeChild(this.ui.canvas);
		document.body.removeChild(this.ui.titleDiv);
		document.body.removeChild(this.ui.infoDiv);
		document.body.removeChild(this.ui.statsDiv);
		document.body.removeChild(this.ui.restartBtn);

		// Recreate network and UI
		this.network = new NeuralNetworkSimulator();
		this.ui = new UI(this.network);

		// Example extensibility: register plugin again after restart
		this.network.registerPlugin((sim) => {
			if (sim.step % 500 === 0) {
				const avgEnergy = sim.nodes.reduce((sum, n) => sum + n.energy, 0) / sim.nodes.length;
				console.log(`[App Research] Step ${sim.step}: Avg energy = ${avgEnergy.toFixed(2)}`);
			}
		});

		// Re-wire restart button
		this.ui.restartBtn.onclick = () => {
			this.ui.restartAllSounds();
			this.restartSimulation();
		};
		this.ui.playPauseBtn.onclick = () => {
			this.manuallyPaused = !this.manuallyPaused;
			this.ui.playPauseBtn.textContent = this.manuallyPaused ? 'Play Simulation' : 'Pause Simulation';
			this.ui.titleDiv.textContent = this.manuallyPaused ? 'Simulation Paused (Manual)' : 'Simulation Running';
			this.ui.titleDiv.style.color = '#fff';
		};

		this.loopActive = true;
		if (typeof this.runMainLoop === 'function') {
			this.runMainLoop(); // <-- Restart main simulation loop
		}
	}

	// Extensibility: Add more plugins, export/import, and research hooks as needed.
}

/*
Why is it not growing or simulating much?
- If SIM_STEPS_PER_FRAME is set to 1, growth will be very slow (one simulation step per animation frame).
- If NeuralNetworkSimulator.simulateEvolution() is only growing nodes every N steps or with high energy, growth will be slow.
- Other growth methods (e.g., forceGrowthEveryStep, evolutionaryGrowthAfterPulse) may also be limited by energy or step conditions.

To speed up growth, increase SIM_STEPS_PER_FRAME, or ensure simulateEvolution always grows at least one node per branch per step.
Check that cycleSimulationData() calls the simulator's cycle() method, which should call simulateEvolution and energizeNodes.
*/

// App logic is already modular and extensible.
// Comments clarify orchestration and extensibility.
// Comments clarify orchestration and extensibility.
